import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Timer, Eye, LayoutTemplate, MoreHorizontal, Pencil, Send, Trash2, Undo2 } from 'lucide-react';
import {
  addMinutes,
  can,
  playerPlan,
  type SessionDetail,
  type SessionPlayerView,
  type TeamSummary,
  type TemplateDetail,
} from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { formatLongDay, formatTime } from '../lib/dates';
import { Alert, Button, Dialog, Field, LoadingBlock, Menu, MenuItem } from '../components/ui';
import { PlanBuilder } from '../components/plan/PlanBuilder';
import { PlayerPlan } from '../components/plan/PlayerPlan';
import { DetailsForm } from '../components/sessions/DetailsForm';
import { StatusBadge } from '../components/sessions/SessionStatus';
import { teamDotStyle } from '../components/sessions/teamColour';
import { SessionReminderLine } from '../components/reminders/Reminders';
import { SessionAttendance, useAttendance } from '../components/roster/SessionAttendance';
import { MyAvailability } from '../components/roster/MyAvailability';
import { TimeField } from '../components/TimeField';

type AnySession = SessionDetail | SessionPlayerView;

export function SessionPage() {
  const { teamId = '', sessionId = '' } = useParams();
  const key = ['session', sessionId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AnySession>(`/teams/${teamId}/sessions/${sessionId}`) });

  if (q.isLoading) return <LoadingBlock />;
  if (q.isError || !q.data) {
    return (
      <div className="space-y-4">
        <Alert>{errorMessage(q.error)}</Alert>
        <Link to="/" className="btn-secondary">
          Back to the schedule
        </Link>
      </div>
    );
  }
  return q.data.view === 'player' ? <PlayerSession s={q.data} /> : <CoachSession s={q.data} />;
}

function Heading({ s, actions }: { s: AnySession; actions?: React.ReactNode }) {
  const end = addMinutes(s.startTime, s.durationMinutes);
  return (
    <>
      <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Schedule
      </Link>
      <header className="mb-6 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-[24px] leading-tight sm:text-[28px]">{s.title}</h1>
            <StatusBadge status={s.status} />
          </div>
          <p className="tnum mt-1.5 text-muted">
            {formatLongDay(s.date)}, {formatTime(s.startTime)} – {formatTime(end)}
            {s.location ? `, ${s.location}` : ''}
          </p>
          <Link to={`/teams/${s.teamId}?tab=sessions`} className="mt-1 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
            <span className="size-2 rounded-full" style={teamDotStyle(s.teamId)} aria-hidden />
            {s.teamName}
          </Link>
          <SessionReminderLine teamId={s.teamId} sessionId={s.id} />
          {s.focus && (
            <p className="mt-3 max-w-[62ch]">
              <span className="font-medium">Focus:</span> {s.focus}
            </p>
          )}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </header>
    </>
  );
}

// ---------- Players and parents ----------

function PlayerSession({ s }: { s: SessionPlayerView }) {
  return (
    <div className="max-w-[720px]">
      <Heading s={s} />
      <MyAvailability teamId={s.teamId} sessionId={s.id} />
      {s.whatToBring && (
        <div className="mb-6 rounded-lg border border-line bg-surface px-4 py-3">
          <p className="text-[13px] font-semibold text-muted">What to bring</p>
          <p className="mt-0.5 whitespace-pre-line">{s.whatToBring}</p>
        </div>
      )}
      <PlayerPlan plan={s.plan} startTime={s.startTime} />
    </div>
  );
}

// ---------- Coaches ----------

function CoachSession({ s }: { s: SessionDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const key = ['session', s.id];
  const base = `/teams/${s.teamId}/sessions/${s.id}`;
  const [editing, setEditing] = useState(false);
  const [dialog, setDialog] = useState<'copy' | 'template' | 'delete' | 'preview' | null>(null);
  const attendance = useAttendance(s.teamId, s.id);

  const refreshLists = () => {
    void qc.invalidateQueries({ queryKey: ['schedule'] });
    void qc.invalidateQueries({ queryKey: ['team-sessions'] });
  };
  const patch = async (body: Record<string, unknown>) => {
    const next = await api.patch<SessionDetail>(base, body);
    qc.setQueryData(key, next);
    refreshLists();
    return next;
  };
  const publish = useMutation({ mutationFn: (status: 'draft' | 'published') => patch({ status }) });
  const remove = useMutation({
    mutationFn: () => api.del(base),
    onSuccess: () => {
      refreshLists();
      navigate('/', { replace: true });
    },
  });

  const actions = s.canEdit ? (
    <>
      <Link to={`${base}/courtside`} className="btn-secondary" title="Run the session from your phone or laptop: roll call, then each drill with a countdown">
        <Timer className="size-4" aria-hidden /> Courtside
      </Link>
      {s.status === 'draft' ? (
        <Button variant="secondary" loading={publish.isPending} onClick={() => publish.mutate('published')} title="Players and parents on the team can see it once it’s published">
          <Send className="size-4" aria-hidden /> Publish to team
        </Button>
      ) : null}
      <Button variant="secondary" onClick={() => setDialog('preview')}>
        <Eye className="size-4" aria-hidden /> What players see
      </Button>
      <Menu label={<><MoreHorizontal className="size-4" aria-hidden /><span className="sr-only">More actions</span></>}>
        {(close) => (
          <>
            <MenuItem onClick={() => { close(); setEditing(true); }}>
              <Pencil className="size-4" aria-hidden /> Edit details
            </MenuItem>
            <MenuItem onClick={() => { close(); setDialog('copy'); }}>
              <Copy className="size-4" aria-hidden /> Copy to another day
            </MenuItem>
            <MenuItem onClick={() => { close(); setDialog('template'); }}>
              <LayoutTemplate className="size-4" aria-hidden /> Save as template
            </MenuItem>
            {s.status === 'published' && (
              <MenuItem onClick={() => { close(); publish.mutate('draft'); }}>
                <Undo2 className="size-4" aria-hidden /> Move back to draft
              </MenuItem>
            )}
            <MenuItem tone="danger" onClick={() => { close(); setDialog('delete'); }}>
              <Trash2 className="size-4" aria-hidden /> Delete session
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  ) : (
    <Button variant="secondary" onClick={() => setDialog('preview')}>
      <Eye className="size-4" aria-hidden /> What players see
    </Button>
  );

  return (
    <>
      <Heading s={s} actions={actions} />
      {publish.isError && (
        <div className="mb-4">
          <Alert>{errorMessage(publish.error)}</Alert>
        </div>
      )}
      {s.status === 'draft' && s.canEdit && (
        <p className="mb-4 text-[14px] text-muted">This is a draft. Coaches can see it; players and parents can’t until you publish it.</p>
      )}

      {editing ? (
        <section className="mb-8 rounded-lg border border-line bg-surface p-4 sm:p-5">
          <h2 className="mb-4 text-[16px] font-semibold">Session details</h2>
          <DetailsForm value={s} onSave={patch} onDone={() => setEditing(false)} />
        </section>
      ) : (
        <Facts s={s} onEdit={s.canEdit ? () => setEditing(true) : undefined} />
      )}

      <SessionAttendance teamId={s.teamId} sessionId={s.id} />

      <PlanBuilder
        key={s.id}
        initialPlan={s.plan}
        version={s.version}
        readOnly={!s.canEdit}
        meta={{ startTime: s.startTime, durationMinutes: s.durationMinutes, courtsAvailable: s.courtsAvailable, playersExpected: s.playersExpected ?? (attendance.data?.rows.length ? attendance.data.expected : null) }}
        save={async (plan, version) => {
          const next = await api.put<SessionDetail>(`${base}/plan`, { version, plan });
          qc.setQueryData(key, next);
          refreshLists();
          return next;
        }}
      />

      <Dialog open={dialog === 'preview'} onClose={() => setDialog(null)} title="What players and parents see">
        <div className="max-h-[65dvh] overflow-y-auto">
          {s.status === 'draft' && <div className="mb-4"><Alert tone="info">Players and parents can’t see this yet. It’s still a draft.</Alert></div>}
          {s.whatToBring && (
            <p className="mb-4">
              <span className="font-medium">What to bring:</span> {s.whatToBring}
            </p>
          )}
          <PlayerPlan plan={playerPlan(s.plan)} startTime={s.startTime} />
          <p className="mt-4 text-[13px] text-muted">Coaching points, coach notes and equipment lists are hidden from them. Save the plan to update this.</p>
        </div>
      </Dialog>
      <CopyDialog s={s} open={dialog === 'copy'} onClose={() => setDialog(null)} />
      <TemplateDialog s={s} open={dialog === 'template'} onClose={() => setDialog(null)} />
      <Dialog open={dialog === 'delete'} onClose={() => setDialog(null)} title={`Delete ${s.title}?`}>
        <p className="text-muted">
          The session on {formatLongDay(s.date)} and its plan will be deleted for everyone. Save it as a template first if you want to reuse the plan.
        </p>
        {remove.isError && <div className="mt-3"><Alert>{errorMessage(remove.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDialog(null)}>
            Keep it
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
            Delete session
          </Button>
        </div>
      </Dialog>
    </>
  );
}

function Facts({ s, onEdit }: { s: SessionDetail; onEdit?: () => void }) {
  const items: [string, string | null][] = [
    ['What to bring', s.whatToBring],
    ['Players expected', s.playersExpected ? String(s.playersExpected) : null],
    ['Courts', s.courtsAvailable ? String(s.courtsAvailable) : null],
    ['Coach notes', s.notes],
  ];
  const shown = items.filter(([, v]) => v);
  if (!shown.length && !onEdit) return null;
  return (
    <section className="mb-8 rounded-lg border border-line bg-surface px-4 py-3">
      {shown.length ? (
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-[repeat(auto-fit,minmax(160px,1fr))]">
          {shown.map(([k, v]) => (
            <div key={k} className={k === 'Coach notes' || k === 'What to bring' ? 'sm:col-span-2' : ''}>
              <dt className="text-[12px] font-semibold text-muted">{k}</dt>
              <dd className="whitespace-pre-line text-[14px]">{v}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="text-[14px] text-muted">Add a location, what to bring, how many players and courts you’ll have, and notes for coaches.</p>
      )}
      {onEdit && (
        <button className="mt-2 text-[14px] font-medium text-action hover:underline" onClick={onEdit}>
          Edit details
        </button>
      )}
    </section>
  );
}

function CopyDialog({ s, open, onClose }: { s: SessionDetail; open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams'), enabled: open });
  const [teamId, setTeamId] = useState(s.teamId);
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState(s.startTime);
  const copy = useMutation({
    mutationFn: () => api.post<SessionDetail>(`/teams/${s.teamId}/sessions/${s.id}/copy`, { teamId, date, startTime }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['schedule'] });
      void qc.invalidateQueries({ queryKey: ['team-sessions'] });
      onClose();
      navigate(`/teams/${c.teamId}/sessions/${c.id}`);
    },
  });
  const f = fieldErrors(copy.error);
  return (
    <Dialog open={open} onClose={onClose} title="Copy to another day">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); copy.mutate(); }} className="space-y-4" noValidate>
        <p className="text-muted">Makes a draft copy with the same details and plan. Unsaved changes to the plan aren’t included.</p>
        {copy.isError && !Object.keys(f).length && <Alert>{errorMessage(copy.error)}</Alert>}
        <div className="space-y-1.5">
          <label htmlFor="copy-team" className="block text-[14px] font-medium">
            Team
          </label>
          <select id="copy-team" className="field-input" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            {(teams.data ?? [{ id: s.teamId, name: s.teamName, role: 'head_coach' } as TeamSummary])
              .filter((t) => can(t.role, 'sessions:write'))
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} error={f.date} required />
          <TimeField label="Starts" value={startTime} onChange={setStartTime} error={f.startTime} />
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={copy.isPending} disabled={!date}>
            Make a copy
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function TemplateDialog({ s, open, onClose }: { s: SessionDetail; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(s.title);
  const create = useMutation({
    mutationFn: () =>
      api.post<TemplateDetail>('/templates', {
        title,
        durationMinutes: s.durationMinutes,
        location: s.location,
        focus: s.focus,
        whatToBring: s.whatToBring,
        notes: s.notes,
        playersExpected: s.playersExpected,
        courtsAvailable: s.courtsAvailable,
        fromSessionId: s.id,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
  const close = () => {
    create.reset();
    onClose();
  };
  return (
    <Dialog open={open} onClose={close} title="Save as template">
      {create.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="ok">Saved. Choose it under “Start from” when you make a new session.</Alert>
          <div className="flex justify-end gap-2">
            <Link to={`/templates/${create.data.id}`} className="btn-secondary">
              Open template
            </Link>
            <Button onClick={close}>Done</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }} className="space-y-4" noValidate>
          <p className="text-muted">Saves this session’s plan and details (not the date) so you can reuse it for any team. Templates are yours; other coaches don’t see them.</p>
          {create.isError && !fieldErrors(create.error).title && <Alert>{errorMessage(create.error)}</Alert>}
          <Field label="Template name" value={title} onChange={(e) => setTitle(e.target.value)} error={fieldErrors(create.error).title} maxLength={120} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Save template
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
